import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { LangContext } from '../App';
import api from '../services/api';

// URL du dashboard admin autonome. Configurable via VITE_ADMIN_URL
// (production : https://admin.chassehunt.ca). Sinon dérivée de l'URL de
// l'API en retirant le segment /api/v1, ce qui donne :
//   dev : http://localhost:3001/admin
//   prod : https://api.chassehunt.ca/admin  (fallback de secours)
const ADMIN_URL =
  import.meta.env.VITE_ADMIN_URL ||
  (import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1').replace(/\/api\/v\d+\/?$/, '') +
    '/admin';

/* ── Role metadata ── */
const ROLES = {
  hunter: { icon: '🏹', accent: '#2a5a3a', fr: 'Chasseur', en: 'Hunter' },
  farmer: { icon: '🌾', accent: '#4a6020', fr: 'Agriculteur', en: 'Farmer' },
  landowner: { icon: '🏡', accent: '#3a5848', fr: 'Propriétaire', en: 'Landowner' },
  guide: { icon: '⛺', accent: '#2a4858', fr: 'Guide', en: 'Guide' },
  trapper: { icon: '🐾', accent: '#583820', fr: 'Trappeur', en: 'Trapper' },
  arms_seller: { icon: '🔧', accent: '#504020', fr: 'Vendeur équipement', en: 'Equipment dealer' },
  admin: { icon: '⚙️', accent: '#3a2858', fr: 'Administrateur', en: 'Administrator' },
};

/* ── Role-based menu cards ── */
const MENUS = {
  hunter: [
    {
      icon: '🗺️',
      fr: { title: 'Terres disponibles', desc: 'Parcourir et réserver des territoires de chasse' },
      en: { title: 'Available lands', desc: 'Browse and reserve hunting territories' },
      to: '/lands',
    },
    {
      icon: '📅',
      fr: { title: 'Mes réservations', desc: 'Consulter vos locations en cours et passées' },
      en: { title: 'My bookings', desc: 'View your current and past rentals' },
      to: '/mes-reservations',
    },
    {
      icon: '🦌',
      fr: { title: 'Observatoire faune', desc: 'Soumettre des observations et gagner des crédits' },
      en: { title: 'Wildlife observatory', desc: 'Submit sightings and earn credits' },
      to: '/wildlife',
    },
    {
      icon: '🏆',
      fr: { title: 'Mes crédits', desc: 'Convertir vos crédits en réductions sur les locations' },
      en: { title: 'My credits', desc: 'Convert your credits into rental discounts' },
      to: '/my-credits',
    },
  ],
  landowner: [
    {
      icon: '🏡',
      fr: { title: 'Mes terres', desc: 'Gérer vos territoires mis en location' },
      en: { title: 'My lands', desc: 'Manage your listed territories' },
      to: '/my-lands',
    },
    {
      icon: '➕',
      fr: { title: 'Enregistrer une terre', desc: 'Mettre un nouveau territoire en location' },
      en: { title: 'Register a property', desc: 'List a new territory for rent' },
      to: '/register-land',
    },
    {
      icon: '📅',
      fr: { title: 'Réservations reçues', desc: 'Gérer les demandes de chasse sur vos terres' },
      en: { title: 'Received bookings', desc: 'Manage hunting requests on your lands' },
      to: '/mes-reservations',
    },
    {
      icon: '💰',
      fr: { title: 'Revenus', desc: 'Suivi de vos paiements et prochains versements' },
      en: { title: 'Revenue', desc: 'Track your payments and upcoming payouts' },
      to: '/mes-revenus',
    },
  ],
  farmer: [
    {
      icon: '🏡',
      fr: { title: 'Mes terres', desc: 'Gérer vos territoires agricoles mis en location' },
      en: { title: 'My lands', desc: 'Manage your agricultural lands for hunting' },
      to: '/my-lands',
    },
    {
      icon: '➕',
      fr: { title: 'Enregistrer une terre', desc: 'Mettre un nouveau territoire en location' },
      en: { title: 'Register a property', desc: 'List a new territory for rent' },
      to: '/register-land',
    },
    {
      icon: '🐗',
      fr: {
        title: 'Signaler dépredation',
        desc: 'Déclarer des dommages causés par la faune sauvage',
      },
      en: { title: 'Report depredation', desc: 'Declare damage caused by wildlife' },
      to: '/depredation',
    },
    {
      icon: '📅',
      fr: { title: 'Réservations reçues', desc: 'Gérer les demandes de chasse sur vos terres' },
      en: { title: 'Received bookings', desc: 'Manage hunting requests on your lands' },
      to: '/mes-reservations',
    },
    {
      icon: '💰',
      fr: { title: 'Revenus', desc: 'Suivi de vos paiements et prochains versements' },
      en: { title: 'Revenue', desc: 'Track your payments and upcoming payouts' },
      to: '/mes-revenus',
    },
  ],
  guide: [
    {
      icon: '⛺',
      fr: { title: 'Mon profil guide', desc: 'Modifier vos services, tarifs et disponibilités' },
      en: { title: 'My guide profile', desc: 'Edit your services, rates and availability' },
      to: '/profil-guide',
    },
    {
      icon: '📅',
      fr: { title: 'Réservations clients', desc: 'Gérer les demandes de guidage' },
      en: { title: 'Client bookings', desc: 'Manage guiding requests' },
      to: '/mes-reservations',
    },
    {
      icon: '⭐',
      fr: { title: 'Mes évaluations', desc: 'Consulter les avis laissés par vos clients' },
      en: { title: 'My reviews', desc: 'View feedback from your clients' },
      to: '/mes-ratings',
    },
    {
      icon: '💰',
      fr: { title: 'Revenus', desc: 'Suivi de vos paiements guides' },
      en: { title: 'Revenue', desc: 'Track your guide payments' },
      to: '/mes-revenus',
    },
  ],
  trapper: [
    {
      icon: '🗺️',
      fr: { title: 'Mes zones', desc: 'Vos territoires de piégeage actifs' },
      en: { title: 'My zones', desc: 'Your active trapping territories' },
      to: '/mes-zones',
    },
    {
      icon: '📋',
      fr: { title: 'Permis & renouvellements', desc: 'Gérer votre permis provincial de piégeage' },
      en: { title: 'License & renewals', desc: 'Manage your provincial trapping license' },
      to: '/mon-permis',
    },
    {
      icon: '🦌',
      fr: { title: 'Observatoire faune', desc: 'Contribuer aux données fauniques' },
      en: { title: 'Wildlife observatory', desc: 'Contribute wildlife data' },
      to: '/wildlife',
    },
  ],
  arms_seller: [
    {
      icon: '📦',
      fr: { title: 'Mon inventaire', desc: "Gérer vos annonces d'équipements et d'armes" },
      en: { title: 'My inventory', desc: 'Manage your equipment and firearm listings' },
      to: '/mon-inventaire',
    },
    {
      icon: '💰',
      fr: { title: 'Mes ventes', desc: 'Historique et suivi de vos transactions' },
      en: { title: 'My sales', desc: 'History and tracking of your transactions' },
      to: '/mes-ventes',
    },
    {
      icon: '📋',
      fr: { title: 'Permis PPA', desc: "Statut de votre autorisation de vente d'armes" },
      en: { title: 'PPA permit', desc: 'Status of your firearms dealer authorization' },
      to: '/mon-permis',
    },
  ],
  // Note : pas d'entrée `admin` ici. L'administration vit sur le dashboard
  // HTML autonome (admin.chassehunt.ca en prod). Pour les rares fois où
  // un admin atterrit sur ce tableau de bord, un bandeau l'oriente — voir
  // le rendu plus bas conditionné par `typeUtilisateur === 'admin'`.
};

/* ── Statut vérification badge ── */
const STATUT_STYLES = {
  verifie: {
    bg: 'rgba(40,160,80,0.15)',
    border: 'rgba(40,160,80,0.4)',
    color: 'var(--couleur-texte-succes)',
    fr: '✓ Vérifié',
    en: '✓ Verified',
  },
  en_attente: {
    bg: 'rgba(201,162,39,0.12)',
    border: 'rgba(201,162,39,0.4)',
    color: 'var(--couleur-or)',
    fr: '⏳ Vérification en attente',
    en: '⏳ Verification pending',
  },
  rejete: {
    bg: 'rgba(200,50,50,0.12)',
    border: 'rgba(200,50,50,0.35)',
    color: 'var(--couleur-texte-erreur)',
    fr: '✗ Vérification refusée',
    en: '✗ Verification rejected',
  },
};

/* ── Translations ── */
const T = {
  fr: {
    bonjour: 'Bonjour',
    bienvenue: 'Bienvenue sur ChasseHunt',
    monCompte: 'Mon compte',
    monProfil: 'Mon profil',
    parametres: 'Paramètres',
    securite: 'Sécurité & 2FA',
    ratings: 'Mes évaluations',
    versAccueil: "Retour à l'accueil",
    pageEnConstruction: 'Cette page est en construction.',
    mesEncans: 'Mes encans actifs',
    mesLandsSection: 'Mes terres',
    mesLandsAjouter: '+ Enregistrer une terre',
    mesLandsAucune: "Vous n'avez aucune terre pour l'instant.",
    mesLandsChargement: 'Chargement…',
    mesLandsErreur: 'Impossible de charger vos terres.',
    mesLandsVoir: 'Voir la terre',
    mesLandsCreerEncan: 'Créer un encan',
    statutActif: 'Actif',
    statutInactif: 'Inactif',
    statutArchive: 'Archivée',
    creerEncan: '+ Créer un nouvel encan',
    aucunEncan: "Vous n'avez aucun encan pour l'instant.",
    chargementEncans: 'Chargement…',
    erreurEncans: 'Impossible de charger vos encans.',
    voirDetail: 'Voir le détail',
    placesRestantes: (r, t) => `${r} / ${t} place${t > 1 ? 's' : ''}`,
    miseActuelle: 'Mise actuelle',
    prixPlancher: 'Prix plancher',
    prixFixe: 'Prix fixe',
    encanTermine: 'Terminé',
    fermeDans: 'Ferme dans',
    statutBrouillon: 'Brouillon',
    statutEnCours: 'En cours',
    statutTerminee: 'Terminé',
    statutAnnulee: 'Annulé',
    statutAdjugee: 'Adjugé',
    jours: 'j',
    heures: 'h',
    minutes: 'min',
    statsSection: 'Statistiques',
    statTerres: 'Terres',
    statActives: 'actives',
    statEncansActifs: 'Auctions actifs',
    statRevenusMois: 'Revenus ce mois',
    statRevenusPrecedent: 'Mois précédent',
    statChasseurs: 'Chasseurs accueillis',
    statAnnee: 'cette année',
    erreurStats: 'Impossible de charger les statistiques.',
    reservationsSection: 'Réservations reçues',
    reservationsCarteSousTitre: 'Voir toutes les réservations sur vos terres.',
    reservationsCarteVoir: 'Voir mes réservations →',
    reservationsCarteCompte: (n) => `${n} réservation${n > 1 ? 's' : ''}`,
    colTerritoire: 'Territoire',
    colChasseur: 'Chasseur',
    colMontant: 'Montant',
    colDate: 'Date',
    colPaiement: 'Paiement',
    aucuneReservation: "Aucune réservation pour l'instant.",
    erreurReservations: 'Impossible de charger vos réservations.',
    paiementEnAttente: 'En attente',
    paiementSucceeded: 'Payé',
    paiementFailed: 'Échec',
    paiementRefunded: 'Remboursé',
    depredationSection: 'Dépredation — Mes signalements',
    filtrerEspece: 'Filtrer par espèce',
    toutesEspeces: 'Toutes les espèces',
    aucunSignalement: "Aucun signalement pour l'instant.",
    erreurDepredation: 'Impossible de charger vos signalements.',
    notificationsSection: 'Notifications récentes',

    // Invite card shown on /my-lands when the user has no landowner/farmer role
    inviteLandsTitle: "Vous n'avez pas encore de rôle propriétaire ou agriculteur",
    inviteLandsBody:
      'Pour mettre en location vos terres de chasse, ajoutez le rôle Propriétaire ou Agriculteur à votre compte. La vérification foncière prend 24-48 h.',
    inviteLandsBtn: 'Ajouter un rôle',
  },
  en: {
    bonjour: 'Hello',
    bienvenue: 'Welcome to ChasseHunt',
    monCompte: 'My account',
    monProfil: 'My profile',
    parametres: 'Settings',
    securite: 'Security & 2FA',
    ratings: 'My reviews',
    versAccueil: 'Back to home',
    pageEnConstruction: 'This page is under construction.',
    mesEncans: 'My active auctions',
    mesLandsSection: 'My lands',
    mesLandsAjouter: '+ Register a land',
    mesLandsAucune: 'You have no lands yet.',
    mesLandsChargement: 'Loading…',
    mesLandsErreur: 'Could not load your lands.',
    mesLandsVoir: 'View land',
    mesLandsCreerEncan: 'Create auction',
    statutActif: 'Active',
    statutInactif: 'Inactive',
    statutArchive: 'Archived',
    creerEncan: '+ Create a new auction',
    aucunEncan: 'You have no auctions yet.',
    chargementEncans: 'Loading…',
    erreurEncans: 'Could not load your auctions.',
    voirDetail: 'View details',
    placesRestantes: (r, t) => `${r} / ${t} place${t > 1 ? 's' : ''}`,
    miseActuelle: 'Current bid',
    prixPlancher: 'Floor price',
    prixFixe: 'Fixed price',
    encanTermine: 'Ended',
    fermeDans: 'Closes in',
    statutBrouillon: 'Draft',
    statutEnCours: 'Live',
    statutTerminee: 'Ended',
    statutAnnulee: 'Cancelled',
    statutAdjugee: 'Awarded',
    jours: 'd',
    heures: 'h',
    minutes: 'min',
    statsSection: 'Statistics',
    statTerres: 'Lands',
    statActives: 'active',
    statEncansActifs: 'Active auctions',
    statRevenusMois: 'Revenue this month',
    statRevenusPrecedent: 'Previous month',
    statChasseurs: 'Hunters hosted',
    statAnnee: 'this year',
    erreurStats: 'Could not load statistics.',
    reservationsSection: 'Reservations received',
    reservationsCarteSousTitre: 'See all bookings on your lands.',
    reservationsCarteVoir: 'View my reservations →',
    reservationsCarteCompte: (n) => `${n} reservation${n > 1 ? 's' : ''}`,
    colTerritoire: 'Territory',
    colChasseur: 'Hunter',
    colMontant: 'Amount',
    colDate: 'Date',
    colPaiement: 'Payment',
    aucuneReservation: 'No reservations yet.',
    erreurReservations: 'Could not load your reservations.',
    paiementEnAttente: 'Pending',
    paiementSucceeded: 'Paid',
    paiementFailed: 'Failed',
    paiementRefunded: 'Refunded',
    depredationSection: 'Depredation — My reports',
    filtrerEspece: 'Filter by species',
    toutesEspeces: 'All species',
    aucunSignalement: 'No reports yet.',
    erreurDepredation: 'Could not load your reports.',
    notificationsSection: 'Recent notifications',

    inviteLandsTitle: "You don't have a landowner or farmer role yet",
    inviteLandsBody:
      'To list your hunting lands, add the Landowner or Farmer role to your account. Land registry verification takes 24-48 h.',
    inviteLandsBtn: 'Add a role',
  },
};

/* ── Helpers auction ── */

const OR = 'var(--couleur-or)';
const VERT_ENC = 'var(--couleur-bordure-forte)';

function libelleStatut(t, status) {
  return (
    {
      brouillon: t.statutBrouillon,
      en_cours: t.statutEnCours,
      terminee: t.statutTerminee,
      annulee: t.statutAnnulee,
      adjugee: t.statutAdjugee,
    }[status] || status
  );
}

function couleurStatut(status) {
  return (
    {
      en_cours: OR,
      brouillon: 'rgba(255,255,255,0.4)',
      terminee: 'rgba(255,255,255,0.3)',
      annulee: 'var(--couleur-texte-erreur)',
      adjugee: 'var(--couleur-texte-succes)',
    }[status] || 'rgba(255,255,255,0.35)'
  );
}

function formatMontant(v, lang) {
  if (v == null) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

function compteARebours(dateFermeture, t) {
  if (!dateFermeture) return null;
  const reste = new Date(dateFermeture).getTime() - Date.now();
  if (isNaN(reste)) return null;
  if (reste <= 0) return { termine: true, label: t.encanTermine };
  const j = Math.floor(reste / 86_400_000);
  const h = Math.floor((reste / 3_600_000) % 24);
  const m = Math.floor((reste / 60_000) % 60);
  let label;
  if (j > 0) label = `${j}${t.jours} ${h}${t.heures}`;
  else if (h > 0) label = `${h}${t.heures} ${m} ${t.minutes}`;
  else label = `${m} ${t.minutes}`;
  return { termine: false, label };
}

/* ── Sous-composant : Invite à ajouter le rôle landowner/farmer ── */

function InviteLandsRole({ t }) {
  return (
    <div
      style={{
        background: 'rgba(201,162,39,0.10)',
        border: '1px solid rgba(201,162,39,0.35)',
        borderRadius: 'var(--rayon)',
        padding: '1.4rem 1.6rem',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1.2rem',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ fontSize: '2rem', lineHeight: 1 }} aria-hidden="true">
        🏡
      </div>
      <div style={{ flex: 1, minWidth: '240px' }}>
        <div
          style={{
            fontFamily: 'Georgia, serif',
            fontSize: '1.1rem',
            color: 'var(--couleur-texte)',
            marginBottom: '0.35rem',
          }}
        >
          {t.inviteLandsTitle}
        </div>
        <div
          style={{
            fontSize: '0.88rem',
            color: 'rgba(255,255,255,0.65)',
            lineHeight: 1.45,
          }}
        >
          {t.inviteLandsBody}
        </div>
      </div>
      <Link
        to="/my-profile"
        style={{
          background: 'var(--couleur-or)',
          color: '#1c2e1e',
          padding: '0.6rem 1.1rem',
          borderRadius: '6px',
          fontSize: '0.88rem',
          fontWeight: 600,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {t.inviteLandsBtn}
      </Link>
    </div>
  );
}

/* ── Sous-composant : Mes encans actifs ── */

// List of *lands* the user owns (not auctions). Used on /my-lands so the page
// reflects inventory rather than active listings. Each card links to the land
// detail page so the owner can manage it (edit, photos, create an auction).
function MesLands({ t, lang, role }) {
  const [lands, setLands] = useState(null);
  const [chargement, setCharge] = useState(true);
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    let annule = false;
    setCharge(true);
    api
      .get('/lands/mes-lands?include_archived=1')
      .then((res) => {
        if (annule) return;
        setLands(Array.isArray(res.data) ? res.data : []);
        setErreur(false);
      })
      .catch(() => {
        if (!annule) {
          setLands([]);
          setErreur(true);
        }
      })
      .finally(() => {
        if (!annule) setCharge(false);
      });
    return () => {
      annule = true;
    };
  }, []);

  const peutCreer = role === 'landowner' || role === 'farmer';
  if (!peutCreer) return null;

  const labelStatut = (s) =>
    ({
      active: t.statutActif,
      inactive: t.statutInactif,
      archivee: t.statutArchive,
    })[s] || s;
  const couleurStatutLand = (s) =>
    ({
      active: 'var(--couleur-texte-succes)',
      inactive: 'rgba(255,255,255,0.45)',
      archivee: 'var(--couleur-texte-erreur)',
    })[s] || 'rgba(255,255,255,0.55)';

  return (
    <div style={{ marginBottom: '2.5rem' }} data-testid="mes-lands-section">
      {/* CTA row — the page title is rendered by <PageHeader> at the top of
          the route, so this row only carries the "+ Enregistrer une terre"
          action, right-aligned to match other admin/list pages. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.9rem' }}>
        <Link
          to="/register-land"
          style={{
            background: OR,
            color: 'var(--couleur-fond-carte)',
            padding: '0.45rem 1rem',
            borderRadius: '6px',
            fontSize: '0.82rem',
            fontWeight: 700,
            textDecoration: 'none',
          }}
        >
          {t.mesLandsAjouter}
        </Link>
      </div>

      {chargement && <div style={STYLE_CHARGEMENT}>{t.mesLandsChargement}</div>}
      {!chargement && erreur && <div style={STYLE_ERREUR}>⚠ {t.mesLandsErreur}</div>}
      {!chargement && !erreur && lands && lands.length === 0 && (
        <div style={STYLE_VIDE_CARD}>{t.mesLandsAucune}</div>
      )}
      {!chargement && !erreur && lands && lands.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '0.8rem',
          }}
        >
          {lands.map((land) => {
            const photo = Array.isArray(land.photos) && land.photos.length ? land.photos[0] : null;
            const couleur = couleurStatutLand(land.status);
            return (
              <Link
                key={land.id}
                to={`/lands/${land.id}`}
                state={{ from: '/my-lands' }}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    display: 'grid',
                    gridTemplateRows: 'auto 1fr',
                    height: '100%',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = `${OR}88`;
                    e.currentTarget.style.background = `${VERT_ENC}33`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                  }}
                >
                  <div
                    style={{
                      aspectRatio: '16 / 9',
                      background: photo
                        ? `url(${photo}) center/cover no-repeat`
                        : `linear-gradient(135deg, rgba(184,134,11,0.18), rgba(255,255,255,0.04))`,
                      borderBottom: '1px solid rgba(255,255,255,0.07)',
                      display: photo ? 'block' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(255,255,255,0.25)',
                      fontSize: '1.8rem',
                    }}
                  >
                    {!photo && '🏞'}
                  </div>
                  <div style={{ padding: '0.9rem 1.1rem 1rem', display: 'grid', gap: '0.5rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.4rem',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          background: `${couleur}22`,
                          border: `1px solid ${couleur}66`,
                          color: couleur,
                          borderRadius: '20px',
                          padding: '2px 9px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                        }}
                      >
                        {labelStatut(land.status)}
                      </span>
                      {land.province && (
                        <span
                          style={{
                            fontSize: '0.68rem',
                            color: 'rgba(255,255,255,0.4)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                          }}
                        >
                          📍 {land.province}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontFamily: 'Georgia, serif',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: 'var(--couleur-texte)',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {land.title || '—'}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)' }}>
                      {land.area_hectares != null ? `${land.area_hectares} ha` : '—'}
                      {land.max_hunters
                        ? ` · ${land.max_hunters} chasseur${land.max_hunters > 1 ? 's' : ''}`
                        : ''}
                    </div>
                    <div
                      style={{
                        fontSize: '0.78rem',
                        color: OR,
                        fontWeight: 600,
                        textAlign: 'right',
                        marginTop: '0.2rem',
                      }}
                    >
                      {t.mesLandsVoir} →
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MesEncansActifs({ t, lang, role }) {
  const [auctions, setEncans] = useState(null);
  const [chargement, setCharge] = useState(true);
  const [erreur, setErreur] = useState(false);
  const [tick, setTick] = useState(0);

  // Rafraîchit l'affichage du compte à rebours toutes les 30 s
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let annule = false;
    setCharge(true);
    api
      .get('/listings/my-auctions')
      .then((res) => {
        if (annule) return;
        setEncans(Array.isArray(res.data) ? res.data : []);
        setErreur(false);
      })
      .catch(() => {
        if (!annule) {
          setEncans([]);
          setErreur(true);
        }
      })
      .finally(() => {
        if (!annule) setCharge(false);
      });
    return () => {
      annule = true;
    };
  }, []);

  // Cacher la section pour les rôles qui ne créent pas d'auctions
  const peutCreer = role === 'landowner' || role === 'farmer';
  if (!peutCreer) return null;

  return (
    <div style={{ marginBottom: '2.5rem' }} data-testid="mes-auctions-section">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.8rem',
          flexWrap: 'wrap',
          marginBottom: '0.9rem',
        }}
      >
        <h2
          style={{
            fontSize: '0.78rem',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.45)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            margin: 0,
          }}
        >
          🏷 {t.mesEncans}
        </h2>
        <Link
          to="/create-auction"
          style={{
            background: OR,
            color: 'var(--couleur-fond-carte)',
            padding: '0.45rem 1rem',
            borderRadius: '6px',
            fontSize: '0.82rem',
            fontWeight: 700,
            textDecoration: 'none',
          }}
        >
          {t.creerEncan}
        </Link>
      </div>

      {chargement && (
        <div
          style={{
            color: 'rgba(255,255,255,0.35)',
            fontSize: '0.85rem',
            padding: '1.2rem',
            textAlign: 'center',
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '8px',
          }}
        >
          {t.chargementEncans}
        </div>
      )}

      {!chargement && erreur && (
        <div
          style={{
            background: 'rgba(200,50,50,0.1)',
            border: '1px solid rgba(200,50,50,0.35)',
            borderRadius: '8px',
            padding: '0.8rem 1rem',
            color: 'var(--couleur-texte-erreur)',
            fontSize: '0.85rem',
          }}
        >
          ⚠ {t.erreurEncans}
        </div>
      )}

      {!chargement && !erreur && auctions && auctions.length === 0 && (
        <div
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: `1px dashed rgba(184,134,11,0.4)`,
            borderRadius: '8px',
            padding: '1.4rem 1rem',
            textAlign: 'center',
            color: 'rgba(255,255,255,0.4)',
            fontSize: '0.88rem',
          }}
        >
          {t.aucunEncan}
        </div>
      )}

      {!chargement && !erreur && auctions && auctions.length > 0 && (
        <div
          // tick référencé pour que le compte à rebours rerend même si auctions[] inchangé
          data-tick={tick}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '0.8rem',
          }}
        >
          {auctions.map((enc) => (
            <CarteEncan key={enc.id} enc={enc} t={t} lang={lang} />
          ))}
        </div>
      )}
    </div>
  );
}

function CarteEncan({ enc, t, lang }) {
  const fermeture = enc.auction_closes_at || enc.end_date;
  const compte = compteARebours(fermeture, t);
  const couleur = couleurStatut(enc.status);
  const capacite = enc.max_seats || enc.terre?.max_hunters || 1;
  const placesRestantes = Math.max(0, capacite - (enc.nb_reservations || 0));
  const photo =
    Array.isArray(enc.terre?.photos) && enc.terre.photos.length > 0 ? enc.terre.photos[0] : null;

  return (
    <Link to={`/auctions/${enc.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '10px',
          overflow: 'hidden',
          height: '100%',
          boxSizing: 'border-box',
          cursor: 'pointer',
          transition: 'all 0.15s',
          display: 'grid',
          gridTemplateRows: 'auto 1fr',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = `${OR}88`;
          e.currentTarget.style.background = `${VERT_ENC}33`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
          e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
        }}
      >
        {/* Thumbnail — featured photo of the land (first entry of photos[]).
            Falls back to a neutral placeholder so empty-land cards aren't
            cropped differently from photo'd ones. */}
        <div
          style={{
            aspectRatio: '16 / 9',
            background: photo
              ? `url(${photo}) center/cover no-repeat`
              : `linear-gradient(135deg, rgba(184,134,11,0.18), rgba(255,255,255,0.04))`,
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            display: photo ? 'block' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.25)',
            fontSize: '1.8rem',
          }}
        >
          {!photo && '🏞'}
        </div>

        <div
          style={{
            padding: '0.9rem 1.1rem 1rem',
            display: 'grid',
            gap: '0.6rem',
          }}
        >
          {/* Statut + mode */}
          <div
            style={{
              display: 'flex',
              gap: '0.4rem',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: '0.68rem',
                fontWeight: 700,
                background: `${couleur}22`,
                border: `1px solid ${couleur}66`,
                color: couleur,
                borderRadius: '20px',
                padding: '2px 9px',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {libelleStatut(t, enc.status)}
            </span>
            <span
              style={{
                fontSize: '0.68rem',
                color: 'rgba(255,255,255,0.4)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {enc.mode === 'enchere' ? '⚖ ' : '🎟 '}
              {enc.mode === 'enchere' ? t.miseActuelle : t.prixFixe}
            </span>
          </div>

          {/* Titre de la terre */}
          <div
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: 'var(--couleur-texte)',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {enc.terre?.title || '—'}
          </div>

          {/* Prix / mise + places */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: '0.6rem',
            }}
          >
            <div>
              <div style={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.35)' }}>
                {enc.mode === 'enchere'
                  ? enc.montant_actuel
                    ? t.miseActuelle
                    : t.prixPlancher
                  : t.prixFixe}
              </div>
              <div style={{ fontSize: '1.15rem', fontWeight: 800, color: OR, lineHeight: 1.1 }}>
                {formatMontant(
                  enc.mode === 'enchere'
                    ? (enc.montant_actuel ?? enc.floor_price)
                    : enc.fixed_price,
                  lang
                )}
              </div>
            </div>

            {enc.mode === 'fixed_price' && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.35)' }}>
                  {t.placesRestantes(placesRestantes, capacite).split(' ').slice(0, 1).join(' ')}
                </div>
                <div
                  style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--couleur-texte)' }}
                >
                  {placesRestantes} / {capacite}
                </div>
              </div>
            )}
          </div>

          {/* Compte à rebours */}
          {compte && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: '0.74rem',
                color: compte.termine ? 'var(--couleur-texte-erreur)' : 'rgba(255,255,255,0.55)',
                background: compte.termine ? 'rgba(200,50,50,0.08)' : 'rgba(184,134,11,0.08)',
                border: `1px solid ${compte.termine ? 'rgba(200,50,50,0.3)' : OR + '44'}`,
                borderRadius: '5px',
                padding: '0.35rem 0.55rem',
              }}
            >
              <span>⏱</span>
              <span style={{ flex: 1 }}>
                {compte.termine ? compte.label : `${t.fermeDans} ${compte.label}`}
              </span>
            </div>
          )}

          {/* Bouton voir le détail */}
          <div
            style={{
              fontSize: '0.78rem',
              color: OR,
              fontWeight: 600,
              textAlign: 'right',
            }}
          >
            {t.voirDetail} →
          </div>
        </div>
      </div>
    </Link>
  );
}

/* ── Styles partagés sections ── */

const TITRE_SECTION = {
  fontSize: '0.78rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.45)',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  margin: '0 0 0.9rem',
};

const STYLE_ERREUR = {
  background: 'rgba(200,50,50,0.1)',
  border: '1px solid rgba(200,50,50,0.35)',
  borderRadius: '8px',
  padding: '0.8rem 1rem',
  color: 'var(--couleur-texte-erreur)',
  fontSize: '0.85rem',
};

const STYLE_VIDE_CARD = {
  background: 'rgba(255,255,255,0.02)',
  border: `1px dashed rgba(184,134,11,0.4)`,
  borderRadius: '8px',
  padding: '1.4rem 1rem',
  textAlign: 'center',
  color: 'rgba(255,255,255,0.4)',
  fontSize: '0.88rem',
};

const STYLE_CHARGEMENT = {
  color: 'rgba(255,255,255,0.35)',
  fontSize: '0.85rem',
  padding: '1.2rem',
  textAlign: 'center',
  background: 'rgba(255,255,255,0.02)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '8px',
};

/* ── Section : Statistiques (proprietaire / agriculteur) ── */

function SectionStatistiques({ t, lang }) {
  const [stats, setStats] = useState(null);
  const [chargement, setCharge] = useState(true);
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    let annule = false;
    api
      .get('/proprietaire/statistiques')
      .then((r) => {
        if (!annule) {
          setStats(r.data);
          setErreur(false);
        }
      })
      .catch(() => {
        if (!annule) setErreur(true);
      })
      .finally(() => {
        if (!annule) setCharge(false);
      });
    return () => {
      annule = true;
    };
  }, []);

  const tiles = stats
    ? [
        {
          icon: '🏡',
          label: t.statTerres,
          value: stats.nombre_terres,
          sub: `${stats.terres_actives} ${t.statActives}`,
        },
        {
          icon: '⚖',
          label: t.statEncansActifs,
          value: stats.active_auctions,
        },
        {
          icon: '💰',
          label: t.statRevenusMois,
          value: formatMontant(stats.revenus_mois_courant, lang),
          amount: true,
        },
        {
          icon: '📈',
          label: t.statRevenusPrecedent,
          value: formatMontant(stats.revenus_mois_precedent, lang),
          amount: true,
          estim: true,
        },
        {
          icon: '🏕',
          label: t.statChasseurs,
          value: stats.chasseurs_accueillis,
          sub: t.statAnnee,
        },
      ]
    : [];

  return (
    <div style={{ marginBottom: '2.5rem' }} data-testid="section-statistiques">
      <h2 style={TITRE_SECTION}>📊 {t.statsSection}</h2>
      {chargement && <div style={STYLE_CHARGEMENT}>{t.chargementEncans}</div>}
      {!chargement && erreur && <div style={STYLE_ERREUR}>⚠ {t.erreurStats}</div>}
      {!chargement && !erreur && stats && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))',
            gap: '0.75rem',
          }}
        >
          {tiles.map((tile, i) => (
            <div
              key={i}
              style={{
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '10px',
                padding: '1rem 1.1rem',
              }}
            >
              <div style={{ fontSize: '1.3rem', marginBottom: '0.35rem' }}>{tile.icon}</div>
              <div
                style={{
                  fontSize: '0.66rem',
                  color: 'rgba(255,255,255,0.38)',
                  marginBottom: '0.2rem',
                  letterSpacing: '0.03em',
                }}
              >
                {tile.label}
              </div>
              <div
                style={{
                  fontSize: tile.amount ? '0.95rem' : '1.6rem',
                  fontWeight: 800,
                  color: tile.estim ? 'rgba(255,255,255,0.35)' : OR,
                  lineHeight: 1,
                }}
              >
                {tile.value ?? '—'}
              </div>
              {tile.sub && (
                <div
                  style={{
                    fontSize: '0.65rem',
                    color: 'rgba(255,255,255,0.28)',
                    marginTop: '0.3rem',
                  }}
                >
                  {tile.sub}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Section : Réservations reçues (proprietaire / agriculteur) ── */

// Compact link card shown on /my-lands in place of the inline reservation table.
// Reads the count (cheap GET) so the card can show "3 reservations" and a hint
// when there's nothing yet — keeps the owner dashboard scannable.
function CarteLienReservations({ t }) {
  const [count, setCount] = useState(null);
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    let annule = false;
    api
      .get('/proprietaire/reservations')
      .then((r) => {
        if (!annule) setCount(Array.isArray(r.data) ? r.data.length : 0);
      })
      .catch(() => {
        if (!annule) setErreur(true);
      });
    return () => {
      annule = true;
    };
  }, []);

  return (
    <div style={{ marginBottom: '2.5rem' }} data-testid="card-reservations-link">
      <Link
        to="/my-reservations"
        style={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'block',
        }}
      >
        <div
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '10px',
            padding: '1.1rem 1.3rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = `${OR}88`;
            e.currentTarget.style.background = `${VERT_ENC}33`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
            e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
            <span style={{ fontSize: '1.6rem' }}>📅</span>
            <div>
              <div
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.05rem',
                  color: 'var(--couleur-texte)',
                  fontWeight: 600,
                }}
              >
                {t.reservationsSection}
              </div>
              <div
                style={{
                  fontSize: '0.82rem',
                  color: 'rgba(255,255,255,0.5)',
                  marginTop: '0.2rem',
                }}
              >
                {erreur
                  ? t.erreurReservations
                  : count == null
                    ? t.reservationsCarteSousTitre
                    : count === 0
                      ? t.aucuneReservation
                      : `${t.reservationsCarteCompte(count)} · ${t.reservationsCarteSousTitre}`}
              </div>
            </div>
          </div>
          <span style={{ color: OR, fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {t.reservationsCarteVoir}
          </span>
        </div>
      </Link>
    </div>
  );
}

/* SectionReservations was lifted into pages/MyReservations.jsx — the
   /my-lands dashboard now links to it via <CarteLienReservations /> above. */

/* ── Section : Dépredation + dropdown espèces (agriculteur) ── */

function SectionDepredation({ t, lang }) {
  const [especes, setEspeces] = useState([]);
  const [species, setEspece] = useState('');
  const [reports, setSignalements] = useState(null);
  const [chargement, setCharge] = useState(true);
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    api
      .get('/depredation/especes')
      .then((r) => setEspeces(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let annule = false;
    setCharge(true);
    api
      .get('/depredation/mes-reports')
      .then((r) => {
        if (!annule) {
          setSignalements(r.data.data ?? []);
          setErreur(false);
        }
      })
      .catch(() => {
        if (!annule) setErreur(true);
      })
      .finally(() => {
        if (!annule) setCharge(false);
      });
    return () => {
      annule = true;
    };
  }, []);

  const visibles = species
    ? (reports ?? []).filter((s) => s.espece_code === species)
    : (reports ?? []);

  return (
    <div style={{ marginBottom: '2.5rem' }} data-testid="section-depredation">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.8rem',
          flexWrap: 'wrap',
          marginBottom: '0.9rem',
        }}
      >
        <h2 style={{ ...TITRE_SECTION, margin: 0 }}>🐗 {t.depredationSection}</h2>
        <select
          value={species}
          onChange={(e) => setEspece(e.target.value)}
          aria-label={t.filtrerEspece}
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: '6px',
            color: 'var(--couleur-texte)',
            padding: '0.4rem 0.85rem',
            fontSize: '0.82rem',
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          <option value="" style={{ background: 'var(--couleur-fond)' }}>
            {t.toutesEspeces}
          </option>
          {especes.map((esp) => (
            <option key={esp.code} value={esp.code} style={{ background: 'var(--couleur-fond)' }}>
              {lang === 'fr' ? esp.fr : esp.en}
            </option>
          ))}
        </select>
      </div>
      {chargement && <div style={STYLE_CHARGEMENT}>{t.chargementEncans}</div>}
      {!chargement && erreur && <div style={STYLE_ERREUR}>⚠ {t.erreurDepredation}</div>}
      {!chargement && !erreur && visibles.length === 0 && (
        <div style={STYLE_VIDE_CARD}>{t.aucunSignalement}</div>
      )}
      {!chargement && !erreur && visibles.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {visibles.map((s, i) => (
            <div
              key={i}
              style={{
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                fontSize: '0.84rem',
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              {s.title || `Signalement #${i + 1}`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Section : Notifications récentes ── */

function SectionNotifications({ t }) {
  const [notifs, setNotifs] = useState(null);
  const [chargement, setCharge] = useState(true);

  useEffect(() => {
    let annule = false;
    api
      .get('/notifications/recentes')
      .then((r) => {
        if (!annule) setNotifs(r.data.data ?? []);
      })
      .catch(() => {
        if (!annule) setNotifs([]);
      })
      .finally(() => {
        if (!annule) setCharge(false);
      });
    return () => {
      annule = true;
    };
  }, []);

  if (!chargement && notifs && notifs.length === 0) return null;

  return (
    <div style={{ marginBottom: '2.5rem' }} data-testid="section-notifications">
      <h2 style={TITRE_SECTION}>🔔 {t.notificationsSection}</h2>
      {chargement && <div style={STYLE_CHARGEMENT}>{t.chargementEncans}</div>}
      {!chargement && notifs && notifs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
          {notifs.map((n, i) => (
            <div
              key={i}
              style={{
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '8px',
                padding: '0.7rem 1rem',
                fontSize: '0.84rem',
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              {n.message || `Notification #${i + 1}`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const COMPTE_MENU = [
  { icon: '👤', to: '/my-profile', fr: 'Mon profil', en: 'My profile' },
  { icon: '🔐', to: '/security', fr: 'Sécurité & 2FA', en: 'Security & 2FA' },
  { icon: '⭐', to: '/my-reviews', fr: 'Mes évaluations', en: 'My reviews' },
];

export default function Dashboard() {
  const { utilisateur } = useAuth();
  const { lang } = useContext(LangContext);
  const location = useLocation();
  const t = T[lang];

  const typeUtilisateur = utilisateur?.user_type;
  const role = ROLES[typeUtilisateur] ?? ROLES.hunter;
  // Si l'utilisateur n'a pas d'entrée dans MENUS (cas admin), on rend une
  // grille vide et on s'appuie sur le bandeau dédié pour l'orienter.
  const cards = MENUS[typeUtilisateur] ?? [];
  const statutStyle = STATUT_STYLES[utilisateur?.verification_status];

  // Lands-related sections gate on either the primary user_type OR an active
  // landowner/farmer role from the multi-role array. Path-aware: on /my-lands
  // a user without the right role sees an invitation card instead of the
  // hunter dashboard content.
  const hasLandsRole =
    typeUtilisateur === 'landowner' ||
    typeUtilisateur === 'farmer' ||
    (utilisateur?.roles || []).some(
      (r) => ['landowner', 'farmer'].includes(r.role) && r.active !== false
    );
  const onMyLandsPath = location.pathname === '/my-lands';

  return (
    <PrivateLayout>
      <div style={{ maxWidth: 880, margin: '0 auto', paddingBottom: '4rem' }}>
        {/* ── Welcome banner — /dashboard only.
            Hidden on /my-lands because the surrounding PrivateLayout already
            shows the user identity in the sidebar and a hero banner here just
            pushes the actual content (land cards) below the fold. ── */}
        {!onMyLandsPath && (
          <div
            style={{
              background: `${role.accent}22`,
              border: `1px solid ${role.accent}55`,
              borderRadius: '12px',
              padding: '1.6rem 2rem',
              marginBottom: '2.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1.2rem',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ fontSize: '2.8rem', lineHeight: 1 }}>{role.icon}</div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div
                style={{
                  fontSize: '0.78rem',
                  color: 'rgba(255,255,255,0.4)',
                  letterSpacing: '0.06em',
                  marginBottom: '0.25rem',
                }}
              >
                {t.bonjour}
              </div>
              <h1
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.6rem',
                  color: 'var(--couleur-texte)',
                  margin: '0 0 0.5rem',
                  lineHeight: 1.2,
                }}
              >
                {utilisateur?.name}
              </h1>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}
              >
                {/* Role badge */}
                <span
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: `${role.accent}44`,
                    border: `1px solid ${role.accent}88`,
                    color: 'var(--couleur-texte)',
                    borderRadius: '20px',
                    padding: '2px 10px',
                  }}
                >
                  {role[lang]}
                </span>
                {/* Verification badge */}
                {statutStyle && (
                  <span
                    style={{
                      fontSize: '0.72rem',
                      background: statutStyle.bg,
                      border: `1px solid ${statutStyle.border}`,
                      color: statutStyle.color,
                      borderRadius: '20px',
                      padding: '2px 10px',
                    }}
                  >
                    {statutStyle[lang]}
                  </span>
                )}
              </div>
            </div>

            <Link
              to="/"
              style={{
                fontSize: '0.8rem',
                color: 'rgba(255,255,255,0.35)',
                textDecoration: 'none',
                alignSelf: 'flex-start',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.65)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
            >
              ← {t.versAccueil}
            </Link>
          </div>
        )}

        {/* ── /my-lands page header. Mirrors the rest of the private-space
            pages so the page title styling is identical everywhere. ── */}
        {onMyLandsPath && <PageHeader icon="🌲" title={t.mesLandsSection} />}

        {/* ── /my-lands invite : user lacks the landowner/farmer role ── */}
        {onMyLandsPath && !hasLandsRole && <InviteLandsRole t={t} />}

        {/* ── Statistiques (landowner / farmer only — dashboard only) ──
            Hidden on /my-lands so that page stays focused on the user's land
            inventory; numbers still surface on /dashboard. */}
        {hasLandsRole && !onMyLandsPath && <SectionStatistiques t={t} lang={lang} />}

        {/* ── /my-lands: list of the owner's lands (incl. archived). ── */}
        {hasLandsRole && onMyLandsPath && <MesLands t={t} lang={lang} role={typeUtilisateur} />}

        {/* ── /dashboard: active auctions card grid. Not shown on /my-lands
            since that page is about land inventory, not listings. ── */}
        {hasLandsRole && !onMyLandsPath && (
          <MesEncansActifs t={t} lang={lang} role={typeUtilisateur} />
        )}

        {/* ── Réservations reçues — lien vers la page dédiée ──
            Used to render inline as a table; moved to /my-reservations so the
            owner dashboard stays focused on land/listing actions. */}
        {hasLandsRole && <CarteLienReservations t={t} lang={lang} />}

        {/* ── Dépredation + dropdown espèces (farmer only) ── */}
        {typeUtilisateur === 'farmer' && <SectionDepredation t={t} lang={lang} />}

        {/* ── Bandeau admin : redirige vers le dashboard HTML autonome ── */}
        {typeUtilisateur === 'admin' && (
          <div
            style={{
              background: 'rgba(201,162,39,0.10)',
              border: '1px solid rgba(201,162,39,0.35)',
              borderRadius: 'var(--rayon)',
              padding: '1rem 1.3rem',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.85rem',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: '1.4rem' }} aria-hidden="true">
              ⚙️
            </span>
            <div style={{ flex: 1, minWidth: '240px' }}>
              <div style={{ fontWeight: 600, color: 'var(--couleur-texte)' }}>
                {lang === 'fr' ? "Espace d'administration séparé" : 'Separate admin space'}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--couleur-texte-attenué)' }}>
                {lang === 'fr'
                  ? "L'administration se fait sur le dashboard dédié."
                  : 'Admin tasks are handled on the dedicated dashboard.'}
              </div>
            </div>
            <a href={ADMIN_URL} className="btn btn-primaire">
              {lang === 'fr' ? "Ouvrir l'admin →" : 'Open admin →'}
            </a>
          </div>
        )}

        {/* ── Role menu cards — /dashboard only.
            Hidden on /my-lands because every card a landowner/farmer would see
            here (Mes terres, Enregistrer une terre, Réservations, Revenus) is
            already on the page as a dedicated section or in the sidebar nav. ── */}
        {!onMyLandsPath && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2.5rem',
            }}
          >
            {cards.map((card) => {
              const contenu = (
                <div
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    padding: '1.3rem',
                    height: '100%',
                    boxSizing: 'border-box',
                    cursor: card.bientot ? 'default' : 'pointer',
                    opacity: card.bientot ? 0.55 : 1,
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                  onMouseEnter={
                    card.bientot
                      ? undefined
                      : (e) => {
                          e.currentTarget.style.borderColor = `${role.accent}88`;
                          e.currentTarget.style.background = `${role.accent}18`;
                        }
                  }
                  onMouseLeave={
                    card.bientot
                      ? undefined
                      : (e) => {
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                          e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                        }
                  }
                >
                  <div style={{ fontSize: '1.8rem', marginBottom: '0.7rem', lineHeight: 1 }}>
                    {card.icon}
                  </div>
                  <div
                    style={{
                      fontSize: '0.92rem',
                      fontWeight: 600,
                      color: 'var(--couleur-texte)',
                      marginBottom: '0.4rem',
                      lineHeight: 1.3,
                    }}
                  >
                    {card[lang].title}
                    {card.bientot && (
                      <span className="admin-badge-bientot">
                        {lang === 'fr' ? 'Bientôt' : 'Soon'}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: '0.76rem',
                      color: 'rgba(255,255,255,0.42)',
                      lineHeight: 1.5,
                    }}
                  >
                    {card[lang].desc}
                  </div>
                  {!card.bientot && (
                    <div
                      style={{
                        marginTop: '0.9rem',
                        fontSize: '0.75rem',
                        color: `${role.accent === '#2a5a3a' ? 'var(--couleur-texte-succes)' : 'var(--couleur-or)'}`,
                        opacity: 0.8,
                      }}
                    >
                      →
                    </div>
                  )}
                </div>
              );
              if (card.bientot) {
                return (
                  <div key={card.to} aria-disabled="true">
                    {contenu}
                  </div>
                );
              }
              return (
                <Link key={card.to} to={card.to} style={{ textDecoration: 'none' }}>
                  {contenu}
                </Link>
              );
            })}
          </div>
        )}

        {/* ── Mon compte section — /dashboard only.
            Hidden on /my-lands: all three links (profil, sécurité, évaluations)
            already live in the sidebar nav so duplicating them here is noise. */}
        {!onMyLandsPath && (
          <div>
            <h2
              style={{
                fontSize: '0.72rem',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.3)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                margin: '0 0 0.9rem',
              }}
            >
              {t.monCompte}
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
                gap: '0.7rem',
              }}
            >
              {COMPTE_MENU.map((item) => (
                <Link key={item.to} to={item.to} style={{ textDecoration: 'none' }}>
                  <div
                    style={{
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '8px',
                      padding: '0.85rem 1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.7rem',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')
                    }
                  >
                    <span style={{ fontSize: '1rem' }}>{item.icon}</span>
                    <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.65)' }}>
                      {item[lang]}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ── Notifications récentes ── */}
        <SectionNotifications t={t} />
      </div>
    </PrivateLayout>
  );
}
